import type { ReactNode, JSX } from "react";
import { useState } from "react";
import { useLocation, Link, useNavigate } from "react-router";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Box,
  ListItemAvatar,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  Typography,
  Collapse,
} from "@mui/material";
import {
  Home as HomeIcon,
  TableChart as TableChartIcon,
  AddCircle as AddCircleIcon,
  Build as BuildIcon,
  BarChart as BarChartIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  List as ListIcon,
  MoreVert as MoreVertIcon,
  Menu as MenuIcon,
  ArrowBack as ArrowBackIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Notes as NotesIcon,
} from "@mui/icons-material";
import { useAuth } from "react-oidc-context";
import { routePermissions, type Route } from "@/auth/routes";
import { usePermissions } from "@/auth/permissions";
import { useAppSelector } from "@/redux-hooks";
import { selectPortName } from "@/port/port-slice";

interface NavBarProps {
  backButton?: boolean;
}

interface NavigationListItem {
  title: string;
  route: Route;
  icon: ReactNode;
  children?: NavigationListItem[];
}

export default function NavBar({ backButton }: NavBarProps): JSX.Element {
  const auth = useAuth();
  const { can } = usePermissions();
  const [userMoreMenuAnchorEl, setUserMoreMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const userMoreMenuOpen = Boolean(userMoreMenuAnchorEl);
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const portName = useAppSelector(selectPortName);
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  function toggleExpanded(route: string): void {
    setExpandedItems((prev) =>
      prev.includes(route)
        ? prev.filter((item) => item !== route)
        : [...prev, route],
    );
  }

  const actionButton = backButton ? (
    <IconButton
      size="large"
      edge="start"
      aria-label="back"
      color="inherit"
      onClick={() => navigate(-1)}
    >
      <ArrowBackIcon />
    </IconButton>
  ) : (
    <IconButton
      size="large"
      edge="start"
      aria-label="menu"
      color="inherit"
      onClick={() => setDrawerOpen(true)}
    >
      <MenuIcon />
    </IconButton>
  );

  function renderNavigationItem(
    item: NavigationListItem,
    depth: number = 0,
  ): JSX.Element | null {
    if (!routePermissions[item.route].every((permission) => can(permission)))
      return null;

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.route);
    const isSelected =
      location.pathname === item.route ||
      (hasChildren &&
        item.children?.some((child) =>
          location.pathname.startsWith(child.route),
        ));

    return (
      <Box key={item.route}>
        <ListItem disablePadding>
          <ListItemButton
            component={hasChildren ? "div" : Link}
            to={hasChildren ? undefined : item.route}
            onClick={
              hasChildren
                ? () => toggleExpanded(item.route)
                : () => setDrawerOpen(false)
            }
            selected={isSelected}
            sx={{ pl: depth * 2 + 2 }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.title} />
            {hasChildren &&
              (isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {item.children!.map((child) =>
                renderNavigationItem(child, depth + 1),
              )}
            </List>
          </Collapse>
        )}
      </Box>
    );
  }

  return (
    <>
      <AppBar position="sticky" color="primary">
        <Toolbar>
          {auth.isAuthenticated && actionButton}
          <Typography variant="h6" marginLeft={2}>
            AssetSide
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 256 }}>
          <List>
            <ListItem
              secondaryAction={
                <IconButton
                  onClick={(e) => {
                    setUserMoreMenuAnchorEl(e.currentTarget);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <Avatar>{auth?.user?.profile.given_name?.[0]}</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={auth?.user?.profile.name}
                secondary={portName}
              />
            </ListItem>
            <Menu
              open={userMoreMenuOpen}
              anchorEl={userMoreMenuAnchorEl}
              onClose={() => {
                setUserMoreMenuAnchorEl(null);
              }}
            >
              <MenuItem
                component="a"
                href={import.meta.env.VITE_ACCOUNT_MANAGEMENT_URL}
              >
                Manage Account
              </MenuItem>
              <MenuItem onClick={() => auth.signoutPopup()}>Sign out</MenuItem>
            </Menu>
          </List>
          <Divider />
          <List>
            {navigationListItems.map((item) => renderNavigationItem(item))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}

const navigationListItems: NavigationListItem[] = [
  {
    title: "Home",
    route: "/home",
    icon: <HomeIcon />,
  },
  {
    title: "Assets Table",
    route: "/assets-table",
    icon: <TableChartIcon />,
  },
  {
    title: "All Notes",
    route: "/all-notes",
    icon: <NotesIcon />,
  },
  {
    title: "Manage Assets",
    route: "/manage-assets",
    icon: <AddCircleIcon />,
  },
  {
    title: "Configuration",
    route: "/configuration",
    icon: <BuildIcon />,
    children: [
      {
        title: "Default Properties",
        route: "/configuration/default-properties",
        icon: <BuildIcon />,
      },
      {
        title: "Loads",
        route: "/configuration/loads",
        icon: <BuildIcon />,
      },
      {
        title: "Inspection Timeline",
        route: "/configuration/inspection-timeline",
        icon: <BuildIcon />,
      },
      {
        title: "Custom Fields",
        route: "/configuration/custom-fields",
        icon: <BuildIcon />,
      },
    ],
  },
  {
    title: "Metrics",
    route: "/metrics",
    icon: <BarChartIcon />,
  },
  {
    title: "Manage Access",
    route: "/manage-access",
    icon: <AdminPanelSettingsIcon />,
  },
  {
    title: "Batch Entry",
    route: "/batch-entry",
    icon: <ListIcon />,
  },
];
